import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Volume2, Check, X, RotateCcw, Award, Star } from 'lucide-react';

interface EndQuizProps {
  currentLanguage: 'english' | 'french';
  speakText: (text: string, lang?: 'en-US' | 'fr-FR') => void;
}

const quizQuestions = [
  {
    question: "_____ is your favorite subject at school?",
    french: "_____ est ta matière préférée à l'école ?",
    options: ["What", "Who", "Where", "When"],
    correct: "What",
    explanation: {
      english: "'What' is used to ask about things, objects, or subjects.",
      french: "'What' est utilisé pour demander des choses, objets ou sujets."
    }
  },
  {
    question: "_____ do you usually wake up on weekdays?",
    french: "_____ te réveilles-tu habituellement en semaine ?",
    options: ["Who", "When", "Where", "Why"],
    correct: "When",
    explanation: {
      english: "'When' is used to ask about time.",
      french: "'When' est utilisé pour demander l'heure."
    }
  },
  {
    question: "_____ lives in your house besides your family?",
    french: "_____ habite dans ta maison en plus de ta famille ?",
    options: ["Who", "What", "Where", "Which"],
    correct: "Who",
    explanation: {
      english: "'Who' is used to ask about people.",
      french: "'Who' est utilisé pour demander des personnes."
    }
  },
  {
    question: "_____ do you go to buy groceries?",
    french: "_____ vas-tu acheter des courses ?",
    options: ["When", "Who", "Where", "What"],
    correct: "Where",
    explanation: {
      english: "'Where' is used to ask about places or locations.",
      french: "'Where' est utilisé pour demander des lieux ou emplacements."
    }
  },
  {
    question: "_____ do you like spending time with your family?",
    french: "_____ aimes-tu passer du temps avec ta famille ?",
    options: ["Where", "When", "Why", "What"],
    correct: "Why",
    explanation: {
      english: "'Why' is used to ask about reasons or causes.",
      french: "'Why' est utilisé pour demander des raisons ou causes."
    }
  },
  {
    question: "_____ sport do you prefer, football or basketball?",
    french: "_____ sport préfères-tu, le football ou le basketball ?",
    options: ["What", "Which", "Who", "Where"],
    correct: "Which",
    explanation: {
      english: "'Which' is used when choosing between specific options.",
      french: "'Which' est utilisé pour choisir entre des options spécifiques."
    }
  },
  {
    question: "_____ backpack is this on the table?",
    french: "_____ sac à dos est-ce sur la table ?",
    options: ["Who", "Whose", "What", "Where"],
    correct: "Whose",
    explanation: {
      english: "'Whose' is used to ask about possession or ownership.",
      french: "'Whose' est utilisé pour demander la possession ou propriété."
    }
  },
  {
    question: "_____ time does your family have dinner together?",
    french: "_____ heure ta famille dîne-t-elle ensemble ?",
    options: ["What", "When", "Where", "Who"],
    correct: "What",
    explanation: {
      english: "'What time' is used to ask about specific times.",
      french: "'What time' est utilisé pour demander des heures spécifiques."
    }
  }
];

const EndQuiz: React.FC<EndQuizProps> = ({ currentLanguage, speakText }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>(new Array(quizQuestions.length).fill(''));
  const [showResults, setShowResults] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);

  const handleAnswerSelect = (answer: string) => {
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestion] = answer;
    setSelectedAnswers(newAnswers);
  };

  const nextQuestion = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setQuizCompleted(true);
      setShowResults(true);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswers(new Array(quizQuestions.length).fill(''));
    setShowResults(false);
    setQuizCompleted(false);
  };

  const calculateScore = () => {
    return selectedAnswers.reduce((score, answer, index) => {
      return score + (answer === quizQuestions[index].correct ? 1 : 0);
    }, 0);
  };

  const getScoreMessage = (score: number) => {
    const percentage = (score / quizQuestions.length) * 100;
    if (percentage >= 90) {
      return {
        english: "Excellent! You've mastered Wh-questions!",
        french: "Excellent ! Vous maîtrisez les questions en anglais !",
        color: "text-green-600",
        stars: 3
      };
    } else if (percentage >= 70) {
      return {
        english: "Good job! You understand most Wh-questions.",
        french: "Bon travail ! Vous comprenez la plupart des questions.",
        color: "text-blue-600",
        stars: 2
      };
    } else {
      return {
        english: "Keep practicing! Review the lesson and try again.",
        french: "Continuez à pratiquer ! Révisez la leçon et réessayez.",
        color: "text-orange-600",
        stars: 1
      };
    }
  };

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const score = calculateScore();
  const scoreMessage = getScoreMessage(score);

  if (quizCompleted && showResults) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card className="border-2 border-primary/20">
          <CardHeader className="text-center bg-gradient-to-r from-primary/10 to-accent/10">
            <CardTitle className="flex items-center justify-center gap-2 text-2xl">
              <Award className="w-8 h-8 text-primary" />
              {currentLanguage === 'english' ? 'Quiz Results' : 'Résultats du Quiz'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                {[...Array(3)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-8 h-8 ${
                      i < scoreMessage.stars ? 'text-yellow-500 fill-current' : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              
              <h3 className="text-4xl font-bold text-primary mb-2">
                {score} / {quizQuestions.length}
              </h3>
              
              <p className={`text-xl font-semibold mb-4 ${scoreMessage.color}`}>
                {currentLanguage === 'english' ? scoreMessage.english : scoreMessage.french}
              </p>
              
              <Badge variant="secondary" className="text-lg px-4 py-2">
                {Math.round((score / quizQuestions.length) * 100)}%
              </Badge>
            </div>

            <div className="space-y-4 mb-8">
              <h4 className="text-lg font-semibold text-center">
                {currentLanguage === 'english' ? 'Review Your Answers' : 'Révisez Vos Réponses'}
              </h4>
              
              {quizQuestions.map((question, index) => (
                <div key={index} className="border rounded-lg p-4 bg-secondary/30">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      {selectedAnswers[index] === question.correct ? (
                        <Check className="w-6 h-6 text-green-600" />
                      ) : (
                        <X className="w-6 h-6 text-red-600" />
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <p className="font-medium mb-2">
                        {question.question.replace('_____', selectedAnswers[index] || '_____')}
                      </p>
                      
                      {selectedAnswers[index] !== question.correct && (
                        <p className="text-sm text-green-600 mb-2">
                          {currentLanguage === 'english' ? 'Correct answer:' : 'Réponse correcte:'} {question.correct}
                        </p>
                      )}
                      
                      <p className="text-sm text-muted-foreground">
                        {currentLanguage === 'english' ? question.explanation.english : question.explanation.french}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-center gap-4">
              <Button onClick={resetQuiz} variant="outline" size="lg">
                <RotateCcw className="w-5 h-5 mr-2" />
                {currentLanguage === 'english' ? 'Try Again' : 'Réessayer'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentQ = quizQuestions[currentQuestion];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-primary mb-4">
          {currentLanguage === 'english' ? 'End-of-Lesson Quiz' : 'Quiz de Fin de Leçon'}
        </h2>
        <p className="text-muted-foreground mb-6">
          {currentLanguage === 'english' 
            ? 'Test your knowledge of Wh-questions with this comprehensive quiz'
            : 'Testez vos connaissances des questions en anglais avec ce quiz complet'}
        </p>
        
        <div className="flex items-center justify-center gap-4 mb-6">
          <Badge variant="secondary">
            {currentLanguage === 'english' ? 'Question' : 'Question'} {currentQuestion + 1} / {quizQuestions.length}
          </Badge>
          <div className="w-64">
            <Progress value={progress} className="h-2" />
          </div>
        </div>
      </div>

      <Card className="border-2 border-primary/20">
        <CardHeader>
          <CardTitle className="text-xl">
            {currentLanguage === 'english' ? 'Choose the correct Wh-word:' : 'Choisissez le bon mot-question :'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="default">English</Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => speakText(currentQ.question.replace('_____', currentQ.correct))}
                >
                  <Volume2 className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xl font-medium">{currentQ.question}</p>
            </div>
            
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="default" className="bg-accent">Français</Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => speakText(currentQ.french, 'fr-FR')}
                >
                  <Volume2 className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xl font-medium text-accent">{currentQ.french}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {currentQ.options.map((option, index) => (
              <Button
                key={index}
                variant={selectedAnswers[currentQuestion] === option ? "default" : "outline"}
                onClick={() => handleAnswerSelect(option)}
                className="h-12 text-lg"
              >
                {option}
              </Button>
            ))}
          </div>

          <div className="flex justify-between items-center">
            <Button
              onClick={prevQuestion}
              variant="outline"
              disabled={currentQuestion === 0}
            >
              {currentLanguage === 'english' ? 'Previous' : 'Précédent'}
            </Button>
            
            <Button
              onClick={nextQuestion}
              disabled={!selectedAnswers[currentQuestion]}
              className="px-8"
            >
              {currentQuestion === quizQuestions.length - 1 
                ? (currentLanguage === 'english' ? 'Finish Quiz' : 'Terminer le Quiz')
                : (currentLanguage === 'english' ? 'Next' : 'Suivant')
              }
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EndQuiz;